/**
 * Wraps a Promise and rejects with TimeoutError if it does not resolve
 * within `ms` milliseconds. The timer is cleared automatically on settle.
 *
 * Usage:
 *   const result = await withTimeout(
 *     supabase.from('profiles').select('id, first_name, last_name').eq('id', id).maybeSingle(),
 *     8_000,
 *   );
 */

export class TimeoutError extends Error {
  constructor(ms: number) {
    super(`Operation timed out after ${ms}ms`);
    this.name = 'TimeoutError';
  }
}

export function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timerId: ReturnType<typeof setTimeout>;

  const timeout = new Promise<never>((_, reject) => {
    timerId = setTimeout(() => reject(new TimeoutError(ms)), ms);
  });

  return Promise.race([promise, timeout]).finally(() => clearTimeout(timerId));
}
